import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import "../../styles/RecipeList.css";
import {useState, useEffect} from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useCookies } from "react-cookie";
import { NavLink } from "react-router-dom";
import RecipeDrawer from "../../components/RecipeDrawer";
import { ThemeProvider, createTheme } from "@mui/material";


export default function RecipesList({ dark, setDark, sidebarOpen, setSidebarOpen }) {

    const toggleTheme = () => setDark(!dark);
    const [cookies, setCookie, removeCookie] = useCookies(["token"]);
    const [recettes, setRecettes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [steps, setSteps] = useState([]);
    const [search, setSearch] = useState("");
    const [filterViande, setFilterViande] = useState("Tous");

    const decodeToken = (token) => {
        try {
            const payload = token.split(".")[1];
            return JSON.parse(atob(payload));
        } catch {
            return null;
        }
    };

    const [user, setUser] = useState(null);

    useEffect(() => {
            if (!cookies.token) return;
    
            const decoded = decodeToken(cookies.token);
            setUser(decoded); // contient { id, username, role, ... }
        }, [cookies.token]);

    const theme = createTheme({
        palette: {
            mode: dark ? "dark" : "light",
            primary: {
                main: "#6fb8af"
            }
        }
    });

    const [form, setForm] = useState({
        titre: "",
        image: "",
        temps: "",
        difficulte: "",
        viande: "",
        ingredients: ""
    });

    const fetchRecettes = async () => {
        try {
            const response = await fetch("https://api.industriegaming.fr/api/recettes/getAll", {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${cookies.token}`,
                }
            });
            const data = await response.json();
            setRecettes(data);
        } catch (error) {
            console.error("Erreur lors du chargement des recettes :", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        fetchRecettes();
    }, []);

    const handleSubmit = async () => {
        try {

            const response = await fetch("https://api.industriegaming.fr/api/recettes/create", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${cookies.token}`
                },
                body: JSON.stringify({
                    titre: form.titre,
                    image: form.image,
                    temps: form.temps,
                    difficulte: form.difficulte,
                    viande: form.viande,
                    ingredients: form.ingredients
                        .split("\n")
                        .map(i => i.trim())
                        .filter(i => i.length > 0),
                    etapes: steps.map((step, index) => ({
                        texte: step.texte.split("\n"),
                        image: step.image,
                        ordre: index,
                        astuce: step.astuce || null
                    }))
                })
            });

            const data = await response.json();

            if (!response.ok) {
                console.error("Erreur API :", data);
                alert("Erreur lors de l'enregistrement");
                return;
            }

            // Succès
            alert("Recette enregistrée !");
            setDrawerOpen(false);

            // Optionnel : recharger la liste des recettes
            if (typeof fetchRecettes === "function") {
                fetchRecettes();
            }

            // Reset du formulaire
            setForm({
                titre: "",
                image: "",
                temps: "",
                difficulte: "",
                viande: ""
            });

            setSteps([]);

        } catch (error) {
            console.error("Erreur lors de l'enregistrement :", error);
            alert("Erreur serveur");
        }
    };



    const addStep = () => {
        setSteps([
            ...steps,
            {
                id: Date.now(),
                texte: "",
                image: ""
            }
        ]);
    };

    const removeStep = (id) => {
        setSteps(steps.filter(step => step.id !== id));
    };

    const updateStep = (id, field, value) => {
        setSteps(
            steps.map(step =>
                step.id === id ? { ...step, [field]: value } : step
            )
        );
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(steps);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setSteps(items);
    };

    const filteredRecipes = recettes.filter((r) => {
        const matchSearch = r.titre.toLowerCase().includes(search.toLowerCase());
        const matchViande = filterViande === "Tous" || r.viande === filterViande;
        return matchSearch && matchViande;
    });

    if (loading) {
        return <p>Chargement des recettes...</p>;
    }


    return (
        <div className={`dashboard ${dark ? "dark" : ""}`}>
            <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="main">
                <Topbar
                    onToggleSidebar={() => setSidebarOpen(true)}
                    onToggleTheme={toggleTheme}
                    dark={dark}
                />

                <div className="content recipes-container">

                    <div className="recipes-header">
                        <h1 className="recipes-title">Nos recettes 🍽️</h1>

                        <div className="recipes-actions">
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Rechercher une recette..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />

                            <select
                                className="filter-select"
                                value={filterViande}
                                onChange={(e) => setFilterViande(e.target.value)}
                            >
                                <option value="Tous">Toutes les viandes</option>
                                <option value="Poulet">Poulet</option>
                                <option value="Bœuf">Bœuf</option>
                                <option value="Porc">Porc</option>
                                <option value="Poisson">Poisson</option>
                                <option value="Végétarien">Végétarien</option>
                                <option value="Vegan">Vegan</option>
                            </select>
                            {(user.role === "Admin" || user.role === "Moderateur") && (
                                <button className="add-recipe-btn" onClick={() => setDrawerOpen(true)}>
                                    + Ajouter une recette
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="recipes-grid">

                        {filteredRecipes.map((recette) => (
                            <div key={recette.id} className="recipe-card-list">
                                <img src={recette.image} alt={recette.titre} className="recipe-card-image"/>

                                <div className="recipe-card-body">
                                    <h3>{recette.titre}</h3>
                                    <p><strong>Temps :</strong> {recette.temps}</p>
                                    <p><strong>Difficulté :</strong> {recette.difficulte}</p>
                                    <p><strong>Viande :</strong> {recette.viande}</p>

                                    <NavLink  to={`/recipe/${recette.id}`} className="recipe-button">
                                        Voir la recette →
                                    </NavLink >
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>
            {drawerOpen && (
                <ThemeProvider theme={theme}>
                    <RecipeDrawer
                        drawerOpen={drawerOpen}
                        setDrawerOpen={setDrawerOpen}
                        form={form}
                        setForm={setForm}
                        steps={steps}
                        addStep={addStep}
                        updateStep={updateStep}
                        removeStep={removeStep}
                        onDragEnd={onDragEnd}
                        handleSubmit={handleSubmit}
                    />
                </ThemeProvider>

                // <div className="drawer-overlay" onClick={() => setDrawerOpen(false)}>
                //     <div className="drawer" onClick={(e) => e.stopPropagation()}>
                //
                //         {/* HEADER FIXE */}
                //         <div className="drawer-header">
                //             <h2>Ajouter une recette</h2>
                //         </div>
                //
                //         {/* CONTENU SCROLLABLE */}
                //         <div className="drawer-content">
                //
                //             {/* TITRE */}
                //             <input
                //                 type="text"
                //                 placeholder="Titre de la recette"
                //                 className="drawer-input"
                //                 value={form.titre}
                //                 onChange={(e) => setForm({...form, titre: e.target.value})}
                //             />
                //
                //             {/* IMAGE PRINCIPALE */}
                //             <input
                //                 type="text"
                //                 placeholder="URL de l'image principale"
                //                 className="drawer-input"
                //                 value={form.image}
                //                 onChange={(e) => setForm({...form, image: e.target.value})}
                //             />
                //
                //             {/* TEMPS */}
                //             <input
                //                 type="text"
                //                 placeholder="Temps de préparation (ex: 25 min)"
                //                 className="drawer-input"
                //                 value={form.temps}
                //                 onChange={(e) => setForm({...form, temps: e.target.value})}
                //             />
                //
                //             {/* DIFFICULTÉ */}
                //             <select
                //                 className="drawer-input"
                //                 value={form.difficulte}
                //                 onChange={(e) => setForm({...form, difficulte: e.target.value})}
                //             >
                //                 <option value="">Difficulté</option>
                //                 <option value="Facile">Facile</option>
                //                 <option value="Moyen">Moyen</option>
                //                 <option value="Difficile">Difficile</option>
                //             </select>
                //
                //             {/* TYPE DE VIANDE */}
                //             <select
                //                 className="drawer-input"
                //                 value={form.viande}
                //                 onChange={(e) => setForm({...form, viande: e.target.value})}
                //             >
                //                 <option value="">Type de viande</option>
                //                 <option value="Poulet">Poulet</option>
                //                 <option value="Bœuf">Bœuf</option>
                //                 <option value="Porc">Porc</option>
                //                 <option value="Poisson">Poisson</option>
                //                 <option value="Végétarien">Végétarien</option>
                //                 <option value="Vegan">Vegan</option>
                //             </select>
                //
                //             {/* INGREDIENTS */}
                //
                //             <textarea
                //                 className="drawer-textarea"
                //                 placeholder="Ingrédients (un par ligne)"
                //                 value={form.ingredients}
                //                 onChange={(e) => setForm({...form, ingredients: e.target.value})}
                //             />
                //
                //             {/* ÉTAPES */}
                //             <h3>Étapes</h3>
                //
                //             <button className="add-step-btn" onClick={addStep}>
                //                 + Ajouter une étape
                //             </button>
                //
                //             <DragDropContext onDragEnd={onDragEnd}>
                //                 <Droppable droppableId="steps">
                //                     {(provided) => (
                //                         <div {...provided.droppableProps} ref={provided.innerRef}>
                //                             {steps.map((step, index) => (
                //                                 <Draggable key={step.id} draggableId={step.id.toString()} index={index}>
                //                                     {(provided) => (
                //                                         <div
                //                                             className="step-editor-item"
                //                                             ref={provided.innerRef}
                //                                             {...provided.draggableProps}
                //                                         >
                //                                             <div
                //                                                 className="step-drag-handle" {...provided.dragHandleProps}>
                //                                                 ☰
                //                                             </div>
                //
                //                                             <div className="step-editor-content">
                //                                                 <input
                //                                                     type="text"
                //                                                     placeholder="URL de l'image"
                //                                                     className="drawer-input"
                //                                                     value={step.image}
                //                                                     onChange={(e) => updateStep(step.id, "image", e.target.value)}
                //                                                 />
                //
                //                                                 <textarea
                //                                                     placeholder="Tâches (une par ligne)"
                //                                                     className="drawer-textarea"
                //                                                     value={step.texte}
                //                                                     onChange={(e) => updateStep(step.id, "texte", e.target.value)}
                //                                                 />
                //
                //                                                 <textarea
                //                                                     placeholder="L'astuce du chef (optionnel)"
                //                                                     className="drawer-textarea"
                //                                                     value={step.astuce || ""}
                //                                                     onChange={(e) => updateStep(step.id, "astuce", e.target.value)}
                //                                                 />
                //
                //                                                 <button
                //                                                     className="remove-step-btn"
                //                                                     onClick={() => removeStep(step.id)}
                //                                                 >
                //                                                     Supprimer
                //                                                 </button>
                //                                             </div>
                //                                         </div>
                //                                     )}
                //                                 </Draggable>
                //                             ))}
                //                             {provided.placeholder}
                //                         </div>
                //                     )}
                //                 </Droppable>
                //             </DragDropContext>
                //
                //         </div>
                //
                //         {/* FOOTER FIXE */}
                //         <div className="drawer-footer">
                //             <button className="drawer-submit" onClick={handleSubmit}>
                //             Enregistrer
                //             </button>
                //         </div>
                //
                //     </div>
                // </div>
            )}
        </div>
    );
}
